import { useCallback, useEffect, useMemo, useState } from 'react'
import { Contact, Heart, Plus } from 'lucide-react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { MasterServerTable } from '@/components/masters/MasterServerTable'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/AuthContext'
import { useBranch } from '@/contexts/BranchContext'
import {
  CUSTOMER_GENDER_OPTIONS,
  fetchCustomersList,
  fetchCustomersPage,
} from '@/lib/api/customers'
import { fetchStylists } from '@/lib/api/stylists'
import { formatE164Display } from '@/lib/phone'
import { hasEffectivePermission, PERMISSIONS } from '@/lib/permissions'
import { SALON_DESK_MASTERS } from '@/lib/salonDeskConfig'
import { cn } from '@/lib/utils'

const EXPORT_COLUMNS = [
  { key: 'name', label: 'Name', getValue: (row) => row.name },
  { key: 'phone', label: 'Phone', getValue: (row) => row.phone },
  { key: 'gender', label: 'Gender', getValue: (row) => row.gender ?? '' },
  ...(SALON_DESK_MASTERS.showCustomerEmail
    ? [{ key: 'email', label: 'Email', getValue: (row) => row.email ?? '' }]
    : []),
  ...(SALON_DESK_MASTERS.showCustomerAllergies
    ? [{ key: 'allergies', label: 'Allergies', getValue: (row) => row.allergies ?? '' }]
    : []),
  ...(SALON_DESK_MASTERS.showCustomerMarketingConsent
    ? [
        {
          key: 'marketing_consent',
          label: 'Marketing consent',
          getValue: (row) => (row.marketing_consent ? 'yes' : 'no'),
        },
      ]
    : []),
  ...(SALON_DESK_MASTERS.showCustomerBlacklist
    ? [{ key: 'blacklist', label: 'Blacklisted', getValue: (row) => (row.blacklist ? 'yes' : 'no') }]
    : []),
]

const FILTER_SELECT_CLASS = cn(
  'h-9 min-w-[8.5rem] rounded-xl border border-border/70 bg-card px-2.5 text-[12px]',
  'focus:outline-none focus:ring-2 focus:ring-ring',
)

export function CustomersPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const { activeBranchId } = useBranch()
  const canCreate = hasEffectivePermission(user, PERMISSIONS.CUSTOMERS_CREATE)
  const canEdit = hasEffectivePermission(user, PERMISSIONS.CUSTOMERS_EDIT)

  const [stylists, setStylists] = useState([])
  const [success, setSuccess] = useState(() => location.state?.success ?? '')
  const [genderFilter, setGenderFilter] = useState('')
  const [stylistFilter, setStylistFilter] = useState('')
  const [marketingFilter, setMarketingFilter] = useState('')
  const [blacklistFilter, setBlacklistFilter] = useState('')
  const [emailFilter, setEmailFilter] = useState('')
  const [allergiesFilter, setAllergiesFilter] = useState('')

  const fetchPage = useCallback((params) => fetchCustomersPage(params), [])

  const extraParams = useMemo(() => {
    /** @type {Record<string, string | number | boolean>} */
    const params = {}

    if (genderFilter) params.gender = genderFilter
    if (stylistFilter) params.preferred_stylist_id = Number(stylistFilter)
    if (marketingFilter !== '') params.marketing_consent = marketingFilter === '1'
    if (blacklistFilter !== '') params.blacklist = blacklistFilter === '1'
    if (emailFilter !== '') params.has_email = emailFilter === '1'
    if (allergiesFilter !== '') params.has_allergies = allergiesFilter === '1'

    return params
  }, [
    allergiesFilter,
    blacklistFilter,
    emailFilter,
    genderFilter,
    marketingFilter,
    stylistFilter,
  ])

  const hasActiveFilters = Object.keys(extraParams).length > 0

  useEffect(() => {
    if (location.state?.success) {
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.pathname, location.state, navigate])

  useEffect(() => {
    if (!SALON_DESK_MASTERS.showCustomerPreferredStylist) {
      return undefined
    }

    let active = true

    async function loadStylists() {
      try {
        const items = await fetchStylists()
        if (active) setStylists(items.filter((item) => item.is_active))
      } catch {
        if (active) setStylists([])
      }
    }

    loadStylists()
    return () => {
      active = false
    }
  }, [activeBranchId])

  function clearFilters() {
    setGenderFilter('')
    setStylistFilter('')
    setMarketingFilter('')
    setBlacklistFilter('')
    setEmailFilter('')
    setAllergiesFilter('')
  }

  return (
    <div className="salon-page py-8">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Contact className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Customers</h1>
            <p className="text-sm text-muted-foreground">
              {SALON_DESK_MASTERS.lite
                ? 'Search and filter guests — open a profile to edit details and visit history.'
                : 'Guest profiles with filters for stylist, consent, allergies, and more.'}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="ghost" asChild>
            <Link to="/customers/retention">
              <Heart className="h-4 w-4" />
              Customer care
            </Link>
          </Button>
          {canCreate ? (
            <Button type="button" asChild>
              <Link to="/customers/new">
                <Plus className="h-4 w-4" />
                New customer
              </Link>
            </Button>
          ) : null}
        </div>
      </div>

      {success ? (
        <p className="mb-4 text-sm text-primary" role="status">
          {success}
        </p>
      ) : null}

      <MasterServerTable
        key={activeBranchId}
        fetchPage={fetchPage}
        extraParams={extraParams}
        getRowKey={(customer) => customer.id}
        onEdit={
          canEdit
            ? (customer) => navigate(`/customers/${customer.id}/edit`)
            : undefined
        }
        emptyLabel="No customers match these filters."
        emptyActionLabel={canCreate ? 'Add customer' : undefined}
        onEmptyAction={canCreate ? () => navigate('/customers/new') : undefined}
        searchPlaceholder="Search name, phone, email…"
        exportColumns={EXPORT_COLUMNS}
        exportFilename="customers.csv"
        fetchAllForExport={(params) => fetchCustomersList(params)}
        filters={
          <div className="flex flex-wrap items-center gap-2">
            <label className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
              Gender
              <select
                value={genderFilter}
                onChange={(event) => setGenderFilter(event.target.value)}
                className={FILTER_SELECT_CLASS}
                aria-label="Filter by gender"
              >
                <option value="">All</option>
                {CUSTOMER_GENDER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            {SALON_DESK_MASTERS.showCustomerPreferredStylist ? (
              <label className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
                Stylist
                <select
                  value={stylistFilter}
                  onChange={(event) => setStylistFilter(event.target.value)}
                  className={FILTER_SELECT_CLASS}
                  aria-label="Filter by preferred stylist"
                >
                  <option value="">All stylists</option>
                  {stylists.map((stylist) => (
                    <option key={stylist.id} value={stylist.id}>
                      {stylist.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}

            {SALON_DESK_MASTERS.showCustomerMarketingConsent ? (
              <label className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
                Marketing
                <select
                  value={marketingFilter}
                  onChange={(event) => setMarketingFilter(event.target.value)}
                  className={FILTER_SELECT_CLASS}
                  aria-label="Filter by marketing consent"
                >
                  <option value="">All</option>
                  <option value="1">Consented</option>
                  <option value="0">No consent</option>
                </select>
              </label>
            ) : null}

            {SALON_DESK_MASTERS.showCustomerEmail ? (
              <label className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
                Email
                <select
                  value={emailFilter}
                  onChange={(event) => setEmailFilter(event.target.value)}
                  className={FILTER_SELECT_CLASS}
                  aria-label="Filter by email"
                >
                  <option value="">All</option>
                  <option value="1">Has email</option>
                  <option value="0">No email</option>
                </select>
              </label>
            ) : null}

            {SALON_DESK_MASTERS.showCustomerAllergies ? (
              <label className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
                Allergies
                <select
                  value={allergiesFilter}
                  onChange={(event) => setAllergiesFilter(event.target.value)}
                  className={FILTER_SELECT_CLASS}
                  aria-label="Filter by allergies"
                >
                  <option value="">All</option>
                  <option value="1">Has allergies</option>
                  <option value="0">None noted</option>
                </select>
              </label>
            ) : null}

            {SALON_DESK_MASTERS.showCustomerBlacklist ? (
              <label className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
                Status
                <select
                  value={blacklistFilter}
                  onChange={(event) => setBlacklistFilter(event.target.value)}
                  className={FILTER_SELECT_CLASS}
                  aria-label="Filter by blacklist status"
                >
                  <option value="">All</option>
                  <option value="0">Active</option>
                  <option value="1">Blacklisted</option>
                </select>
              </label>
            ) : null}

            {hasActiveFilters ? (
              <button
                type="button"
                className="text-[12px] font-medium text-primary hover:underline"
                onClick={clearFilters}
              >
                Clear filters
              </button>
            ) : null}
          </div>
        }
        columns={[
          { key: 'name', label: 'Name', sortable: true, render: (row) => row.name },
          {
            key: 'phone',
            label: 'Phone',
            sortable: true,
            render: (row) => formatE164Display(row.phone),
          },
          {
            key: 'gender',
            label: 'Gender',
            render: (row) =>
              CUSTOMER_GENDER_OPTIONS.find((option) => option.value === row.gender)?.label ??
              row.gender,
          },
          ...(SALON_DESK_MASTERS.showCustomerPreferredStylist
            ? [
                {
                  key: 'preferred_stylist',
                  label: 'Preferred stylist',
                  render: (row) => row.preferred_stylist?.name || '—',
                },
              ]
            : []),
          ...(SALON_DESK_MASTERS.showCustomerLoyaltyPoints
            ? [
                {
                  key: 'loyalty_points_balance',
                  label: 'Points',
                  render: (row) =>
                    row.loyalty_points_balance != null ? `${row.loyalty_points_balance} pts` : '—',
                },
              ]
            : []),
          ...(SALON_DESK_MASTERS.showCustomerEmail
            ? [{ key: 'email', label: 'Email', render: (row) => row.email || '—' }]
            : []),
          ...(SALON_DESK_MASTERS.showCustomerMarketingConsent
            ? [
                {
                  key: 'marketing_consent',
                  label: 'Marketing',
                  render: (row) => (row.marketing_consent ? 'Yes' : 'No'),
                },
              ]
            : []),
          ...(SALON_DESK_MASTERS.showCustomerBlacklist
            ? [
                {
                  key: 'blacklist',
                  label: 'Status',
                  render: (row) => (row.blacklist ? 'Blacklisted' : 'Active'),
                },
              ]
            : []),
        ]}
      />
    </div>
  )
}
